import { ALL_DEFAULT_EXPERIENCES } from '~/features-shared/experience/config/experiences'
import {
  CUSTOMIZABLE_ICON_COLOR_EXPERIENCES,
  type ExperienceName,
  type IExperience,
} from '~/features-shared/experience/interfaces/experience'

import { useTranslations } from '~/features-generic/translations/composables/translations'

import type { NlsExperiences } from '~/typings/nls/experiences'

/**
 * Retrieve the default experience based on the experience name.
 *
 * @param experienceName - The name of the experience to retrieve.
 * @returns Promise with the default experience.
 */
export async function getDefaultExperience(experienceName: string): Promise<IExperience> {
  const experience: IExperience | undefined = ALL_DEFAULT_EXPERIENCES.find(({ name }) => name === experienceName)

  if (!experience)
    throw new Error('No experience')

  const { $i18n, promise } = useTranslations('experiences')

  await promise

  return {
    ...experience,
    label: $i18n(experience.label as keyof NlsExperiences),
  }
}

/**
 * Retrieve the default experience based on the experience name,
 * without the experience label translation.
 *
 * @param experienceName - The name of the experience to retrieve.
 * @returns The default experience without NLS label.
 */
export function getDefaultExperienceWithoutNLS(experienceName: string): IExperience {
  const experience: IExperience | undefined = ALL_DEFAULT_EXPERIENCES.find(({ name }) => name === experienceName)

  if (!experience)
    throw new Error('No experience')

  return experience
}

/**
 *
 * Get icon and color regarding an experience type.
 *
 * @param experience - The experience.
 * @returns Icon and color, or undefined is the experience support customizable icon and color.
 */
export function getDefaultExperienceIconAndColor(experience: ExperienceName):
{ icon: string, color: string } | undefined {
  const defaultExperience = getDefaultExperienceWithoutNLS(experience)

  if (CUSTOMIZABLE_ICON_COLOR_EXPERIENCES.includes(experience))
    return undefined

  return {
    icon: defaultExperience.icon,
    color: defaultExperience.color,
  }
}
